import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Apple, Play } from "lucide-react";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/lib/links";
import { trackAndroid, trackIos } from "@/lib/stats";
import StoreQr from "@/components/StoreQr";

const STORAGE_KEY = "fp_release_announcement_seen";

function hasSeen() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    /* intentionally ignored: storage blocked – treat as seen so the modal never nags on every page */
    return true;
  }
}

function markSeen() {
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* intentionally ignored: storage blocked (private mode) – modal simply shows again next visit */
  }
}

export default function ReleaseAnnouncement() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (hasSeen()) return;
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    markSeen();
    setOpen(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="release-announcement-overlay"
          className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          data-testid="release-announcement"
          aria-describedby="release-announcement-desc"
          className="fixed inset-x-0 bottom-0 z-[100] mx-auto flex max-h-[92dvh] w-full max-w-[620px] flex-col overflow-hidden rounded-t-[28px] border border-white/10 bg-[#0b0b0d] shadow-[0_-20px_80px_-20px_rgba(139,92,246,0.45)] outline-none duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-8 data-[state=open]:slide-in-from-bottom-8 sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[90vh] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[28px] sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B5CF6] to-transparent"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[420px] -translate-x-1/2 rounded-full bg-[#8B5CF6]/25 blur-[90px]"
          />

          <DialogPrimitive.Close
            data-testid="release-announcement-close"
            aria-label="Ankündigung schließen"
            className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/70 transition-colors hover:border-[#8B5CF6]/60 hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
          >
            <X className="h-[18px] w-[18px]" />
          </DialogPrimitive.Close>

          <div data-lenis-prevent className="relative overflow-y-auto px-6 pb-7 pt-9 sm:px-10 sm:pb-6 sm:pt-9">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FestivalPlan Logo" className="h-11 w-11 rounded-xl object-cover" />
              <div>
                <p className="font-display text-sm font-semibold tracking-tight text-white">FestivalPlan</p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#A78BFA]">Offizieller Release</p>
              </div>
            </div>

            <DialogPrimitive.Title
              data-testid="release-announcement-title"
              className="font-display mt-6 text-2xl font-bold leading-tight tracking-tight text-white sm:text-3xl"
            >
              🎪 FESTIVALPLAN IST JETZT OFFIZIELL VERFÜGBAR!
            </DialogPrimitive.Title>

            <div id="release-announcement-desc" className="mt-4 space-y-3 text-sm leading-relaxed text-white/70 sm:text-base">
              <p className="font-display text-base font-semibold text-white sm:text-lg">
                Deine Crew. Dein Festival. Eure Planung.
              </p>
              <p>
                Seit Oktober 2026 ist FestivalPlan offiziell für{" "}
                <span className="text-white">Android</span> und <span className="text-white">Apple iOS</span> verfügbar!
              </p>
              <p>
                Plane Aufgaben und Mitbringsel, organisiere Fahrgemeinschaften, verwalte eure Gruppenkasse
                und behaltet mit deiner Crew den Überblick – alles an einem Ort.
              </p>
              <p className="text-white/85">
                Keine Testphase mehr. Keine Testeinladung mehr. Einfach herunterladen und gemeinsam losplanen!
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackAndroid(); close(); }}
                data-testid="release-announcement-android"
                className="btn-primary inline-flex flex-1 items-center justify-center gap-2.5 whitespace-nowrap px-5 py-3.5 text-sm font-medium"
              >
                <Play className="h-4 w-4" />
                Für Android herunterladen
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackIos(); close(); }}
                data-testid="release-announcement-ios"
                className="inline-flex flex-1 items-center justify-center gap-2.5 whitespace-nowrap rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 px-5 py-3.5 text-sm font-medium text-white transition-colors hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/20"
              >
                <Apple className="h-4 w-4" />
                Für iPhone herunterladen
              </a>
            </div>

            <div data-testid="release-announcement-qr" className="mt-6 hidden items-center justify-between gap-6 border-t border-white/10 pt-5 sm:flex">
              <p className="max-w-[200px] text-xs uppercase leading-relaxed tracking-[0.2em] text-white/50">
                Am Desktop? Einfach mit dem Handy scannen
              </p>
              <div className="flex items-start gap-6">
                <StoreQr value={GOOGLE_PLAY_URL} label="Google Play" size={76} testId="release-qr-android" />
                <StoreQr value={APP_STORE_URL} label="App Store" size={76} testId="release-qr-ios" />
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              data-testid="release-announcement-dismiss"
              className="mt-4 w-full text-center text-xs text-white/45 underline-offset-4 transition-colors hover:text-white/80 hover:underline"
            >
              Später – weiter zur Website
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
